// Agent-facing connection quality for the softphone widget: the browser's
// network estimate at all times, plus the live media path while a call is up.

type ConnectionQualityLevel = 'good' | 'fair' | 'poor' | 'offline';

// SIP registration as the bars see it. Note the scope: this is the browser
// Registerer's own view, which the switch can contradict — FreeSWITCH can
// expire a binding without telling the client, leaving the Registerer sitting
// "Registered" over a dead line (see the pool-driven re-REGISTER in
// use-softphone). Client-observed unregistration only.
export type RegistrationSnapshot =
  // This tab's phone holds a binding.
  | { status: 'registered' }
  // Nothing to say: no runtime lease, no runtime started yet, or a seat the
  // server has disabled. Never a fault — a page load must not read red.
  | { status: 'unknown' }
  // Down, or never up, for this long on the caller's clock. `retrying` is
  // whether the CRM actually has another attempt coming.
  | { status: 'unregistered'; forMs: number; retrying: boolean }
  // A mirror tab adopting the owner tab's verdict. The owner already applied
  // the grace window against its own clock; a mirror cannot re-time a
  // registration it does not hold, so it takes the answer as given.
  | { status: 'mirrored'; down: boolean; retrying: boolean };

// Media stats for the call in progress over the last sampling window.
export interface CallLinkQuality {
  packetLossPct: number;
  jitterMs: number;
  rttMs: number | null;
}

export interface ConnectionQuality {
  level: ConnectionQualityLevel;
  // Network Information API estimate; null where the browser has none.
  rttMs: number | null;
  downlinkMbps: number | null;
  call: CallLinkQuality | null;
  // SIP transport drops since this page loaded.
  reconnects: number;
  // The phone has been unregistered past the grace window below: it can neither
  // place nor receive calls, whatever the network numbers say.
  registrationDown: boolean;
  // Only meaningful alongside registrationDown: the CRM has another attempt
  // scheduled or in flight. Without one the phone stays down until something
  // else changes, which the agent needs told differently.
  registrationRetrying: boolean;
}

// Cumulative inbound-audio counters from RTCPeerConnection.getStats().
export interface CallLinkCounters {
  packetsReceived: number;
  packetsLost: number;
  jitterMs: number;
  rttMs: number | null;
}

// PH agents sit ~200-250 ms from the US server and Chrome's bandwidth
// estimate often reads ~1.45 Mbps on a link that carries calls fine, so
// "good" has to include both. The poor cut-offs are where audio breaks up.
const FAIR = { rttMs: 400, downlinkMbps: 1, lossPct: 2, jitterMs: 30 };
const POOR = { rttMs: 800, downlinkMbps: 0.4, lossPct: 5, jitterMs: 60 };

// Sized off 453 real drop-to-recover pairs on the floor (within-session client
// clock diffs, Sep 15-18): p50 1.9s, p90 10.9s, p95 17.3s. The tail past the
// p95 runs 20.6 21.2 21.5 22.4 25.7 28.1 29.5, then jumps 55.8 -> 126.1, and
// every genuine outage in the sample ran from 2 minutes to 2.4 hours. So the
// 20-30s band is entirely heals that complete on their own: flagging there
// flashes red and clears, and tells an agent his phone is broken about six
// seconds before it comes back. 30s sits in the gap above the knee, where
// nothing recovers.
const REGISTRATION_GRACE_MS = 30_000;

type Grade = 0 | 1 | 2;

function tooHigh(value: number | null, fair: number, poor: number): Grade {
  if (value === null) return 0;
  return value >= poor ? 2 : value >= fair ? 1 : 0;
}

function tooLow(value: number | null, fair: number, poor: number): Grade {
  if (value === null) return 0;
  return value < poor ? 2 : value < fair ? 1 : 0;
}

const LEVEL_BY_GRADE: Record<Grade, ConnectionQualityLevel> = { 0: 'good', 1: 'fair', 2: 'poor' };

// The registration clock the bars read, advanced at every transition. Lives
// here beside the grace window it feeds, rather than inline in the hook, so the
// two rules that matter are testable.
//
// First: a restart inside an outage keeps the original stamp. Without that, the
// retry loop — which tears the runtime down and rebuilds it every few seconds —
// would reset the window on every pass and never reach the grace, and an
// attempt that has never once reached Registered would have no clock at all.
//
// Second, and only because of the first: a tab that is not the phone holds no
// clock. It has to be cleared rather than merely left unstamped, since a stamp
// made while the tab WAS the phone would otherwise sit there through a morning
// of mirroring and be inherited, hours deep, the moment the tab is promoted —
// rendering red through an otherwise healthy 1-3s boot.
export function registrationDownSince(input: {
  previous: number | null;
  isPhone: boolean;
  registered: boolean;
  nowMs: number;
}): number | null {
  if (!input.isPhone || input.registered) {
    return null;
  }
  return input.previous ?? input.nowMs;
}

// The clock to grade this tab's registration against, or null when it has no
// registration worth reporting. Each mask is a state where a clock may be
// running but says nothing the agent should act on, and each reads as a false
// alarm if it is missed:
//   - device fault: the startup paths that hit one return without registering
//     AND without scheduling a retry, so an unmasked clock would escalate
//     ("nothing is retrying, tell your team lead") 30s after an agent unplugs
//     his headset — while the widget's own status line says "No microphone
//     detected", which is both the proximate cause and the actual fix.
//   - disabled seat: no phone to be unregistered; the widget says so in words.
// A null clock needs no mask: no runtime of this tab's own has ever tried.
export function reportableRegistrationClock(input: {
  downSinceMs: number | null;
  deviceFault: boolean;
  seatDisabled: boolean;
}): number | null {
  if (input.deviceFault || input.seatDisabled) {
    return null;
  }
  return input.downSinceMs;
}

// The graded registration verdict. Exported because the owner tab publishes it
// to mirrors, which cannot time a binding they do not hold — and `retrying` is
// gated on `down` here rather than at each reader so that a routine
// re-registration, which the floor sees some seven times an agent-day, does not
// churn the verdict (and a bus snapshot with it) while nothing is wrong.
export function registrationVerdict(registration: RegistrationSnapshot): { down: boolean; retrying: boolean } {
  switch (registration.status) {
    case 'unregistered': {
      const down = registration.forMs >= REGISTRATION_GRACE_MS;
      return { down, retrying: down && registration.retrying };
    }
    case 'mirrored':
      return { down: registration.down, retrying: registration.down && registration.retrying };
    default:
      return { down: false, retrying: false };
  }
}

export function rateConnection(input: {
  online: boolean;
  transportDown: boolean;
  registration: RegistrationSnapshot;
  rttMs: number | null;
  downlinkMbps: number | null;
  call: CallLinkQuality | null;
  reconnects: number;
}): ConnectionQuality {
  // Chrome reports rtt 0 when it has no estimate yet (typically right after
  // coming back online). That is "unknown", not a perfect link.
  const rttMs = input.rttMs === 0 ? null : input.rttMs;
  // A phone with no SIP binding cannot place or receive a call, so registration
  // outranks the link numbers entirely: 50 ms over 10 Mbps to a switch that has
  // no line for you is not a good connection.
  const fault = registrationVerdict(input.registration);
  const base = {
    rttMs,
    downlinkMbps: input.downlinkMbps,
    call: input.call,
    reconnects: input.reconnects,
    registrationDown: fault.down,
    registrationRetrying: fault.retrying,
  };
  if (!input.online) {
    return { level: 'offline', ...base };
  }
  if (input.transportDown || fault.down) {
    return { level: 'poor', ...base };
  }
  const grade = Math.max(
    tooHigh(rttMs, FAIR.rttMs, POOR.rttMs),
    tooLow(input.downlinkMbps, FAIR.downlinkMbps, POOR.downlinkMbps),
    tooHigh(input.call?.packetLossPct ?? null, FAIR.lossPct, POOR.lossPct),
    tooHigh(input.call?.jitterMs ?? null, FAIR.jitterMs, POOR.jitterMs),
    tooHigh(input.call?.rttMs ?? null, FAIR.rttMs, POOR.rttMs)
  ) as Grade;
  return { level: LEVEL_BY_GRADE[grade], ...base };
}

// Loss over the window between two counter readings. Under ~1 s of audio
// (50 packets at 20 ms) says nothing about the link, so the caller keeps the
// previous reading.
export function callLinkQualityBetween(previous: CallLinkCounters, next: CallLinkCounters): CallLinkQuality | null {
  const received = next.packetsReceived - previous.packetsReceived;
  // Chrome can revise packetsLost downwards when late packets arrive.
  const lost = Math.max(0, next.packetsLost - previous.packetsLost);
  const total = received + lost;
  if (total < 50) {
    return null;
  }
  return { packetLossPct: (lost / total) * 100, jitterMs: next.jitterMs, rttMs: next.rttMs };
}

// Reads only what the bar needs; the full telemetry sample lives in use-softphone.
export async function readCallLinkCounters(peerConnection: RTCPeerConnection): Promise<CallLinkCounters | null> {
  const report = await peerConnection.getStats();
  let inbound: RTCInboundRtpStreamStats | null = null;
  let selectedPairId: string | null = null;
  for (const stat of report.values()) {
    if (stat.type === 'inbound-rtp' && (stat as RTCInboundRtpStreamStats).kind === 'audio') {
      inbound = stat as RTCInboundRtpStreamStats;
    }
    if (stat.type === 'transport') {
      selectedPairId = (stat as RTCTransportStats).selectedCandidatePairId ?? null;
    }
  }
  if (!inbound) {
    return null;
  }
  const pair = selectedPairId ? (report.get(selectedPairId) as RTCIceCandidatePairStats | undefined) : undefined;
  return {
    packetsReceived: inbound.packetsReceived ?? 0,
    packetsLost: inbound.packetsLost ?? 0,
    jitterMs: (inbound.jitter ?? 0) * 1000,
    rttMs: typeof pair?.currentRoundTripTime === 'number' ? pair.currentRoundTripTime * 1000 : null,
  };
}

// Rounded so the widget re-renders on visible change only, not on every
// jittering estimate.
export function connectionQualitySignature(quality: ConnectionQuality): string {
  return [
    quality.level,
    quality.registrationDown ? (quality.registrationRetrying ? 'unreg-retry' : 'unreg') : '',
    quality.reconnects,
    quality.rttMs === null ? '' : Math.round(quality.rttMs / 25),
    quality.downlinkMbps === null ? '' : quality.downlinkMbps.toFixed(1),
    quality.call ? quality.call.packetLossPct.toFixed(1) : '',
    quality.call ? Math.round(quality.call.jitterMs) : '',
    quality.call?.rttMs == null ? '' : Math.round(quality.call.rttMs / 10),
  ].join('|');
}
